import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  AlertCircle,
  Check,
  CheckCircle,
  RefreshCw,
  TestTube,
  Brain,
  Zap,
  DollarSign,
  Activity,
  Shield,
  Info,
  Loader2,
  Plus,
  X,
} from "lucide-react";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { isPlatformOrg, isSuperAdmin } from "@/lib/roles";
import { useSortableData } from "@/hooks/useSortableData";
import {
  automationApi,
  LLMProvider,
  QuorumRule,
} from "@/services/api/automation";
import { toast } from "sonner";

export const Route = createFileRoute("/portal/automation/llm")({
  component: LLMConfiguration,
});

interface ProviderConfig {
  provider: LLMProvider;
  is_enabled: boolean;
  credentials_configured: boolean;
  health_status?: "healthy" | "degraded" | "unavailable" | "active";
  health_latency_ms?: number;
  models?: unknown[];
}

interface ModelPreference {
  task_type: string;
  primary_model: string;
  primary_model_name: string;
  fallback_models: string[];
  fallback_model_names: string[];
  quality_tier: string;
  max_retries: number;
}

function LLMConfiguration() {
  const { token, session } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const showSystemConfig =
    isSuperAdmin(session) && isPlatformOrg(selectedOrganisation);
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [preferences, setPreferences] = useState<ModelPreference[]>([]);
  const [models, setModels] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [usageSummary, setUsageSummary] = useState<{
    total_calls: number;
    total_tokens: number;
    total_cost: number;
  } | null>(null);
  const [usageByProvider, setUsageByProvider] = useState<
    Array<{
      provider_id: string;
      provider_name: string;
      total_calls: number;
      total_tokens: number;
      prompt_tokens: number;
      completion_tokens: number;
      avg_latency_ms: number;
      total_cost: number;
    }>
  >([]);
  const [usageByModel, setUsageByModel] = useState<
    Array<{
      model_id: string;
      model_name: string;
      display_name: string;
      provider_name: string;
      total_calls: number;
      total_tokens: number;
      prompt_tokens: number;
      completion_tokens: number;
      avg_latency_ms: number;
      total_cost: number;
    }>
  >([]);
  const [selectedProviderFilter, setSelectedProviderFilter] =
    useState<string>("");
  const [systemDefaults, setSystemDefaults] = useState<ModelPreference[]>([]);
  const [quorumRules, setQuorumRules] = useState<QuorumRule[]>([]);
  const [consensusStrategies, setConsensusStrategies] = useState<string[]>([]);
  const [editingQuorumRule, setEditingQuorumRule] = useState<QuorumRule | null>(
    null,
  );

  // Sortable data for tables
  const { sortedData: sortedUsageByProvider, requestSort: requestProviderSort, getSortDirection: getProviderSortDir } =
    useSortableData(usageByProvider, { initialSort: { key: "total_calls", direction: "desc" } });
  const { sortedData: sortedUsageByModel, requestSort: requestModelSort, getSortDirection: getModelSortDir } =
    useSortableData(usageByModel, { initialSort: { key: "total_calls", direction: "desc" } });
  const { sortedData: sortedQuorumRules, requestSort: requestQuorumSort, getSortDirection: getQuorumSortDir } =
    useSortableData(quorumRules, { initialSort: { key: "task_type", direction: "asc" } });

  // Format cost with scientific notation for small values
  const formatCost = (cost: number | string) => {
    const numCost = Number(cost || 0);
    if (numCost === 0) return "$0.00";
    if (numCost < 0.01) {
      return `$${numCost.toExponential(2)}`;
    } else {
      return `$${numCost.toFixed(4)}`;
    }
  };

  // Reload model usage when provider filter changes
  useEffect(() => {
    if (token) {
      loadUsageData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProviderFilter]);

  useEffect(() => {
    if (token) {
      const initialize = async () => {
        await loadProviders();
        const modelsMap = await loadModels();
        await loadPreferences(modelsMap);
        await loadSystemDefaults(modelsMap);
        await loadUsageData();
        await loadQuorumRules();
        await loadConsensusStrategies();
      };
      initialize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadProviders = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await automationApi.llmConfig.listProviders(token);
      const providerData = (response as unknown[]).map((p: unknown) => {
        const provider = p as Record<string, unknown>;
        const config = provider.config as Record<string, unknown> | undefined;
        return {
          provider: {
            id: provider.id as string,
            organisation_id: provider.organisation_id as string,
            provider_name: provider.name as string,
            provider_type:
              provider.provider_type as LLMProvider["provider_type"],
            model_name: provider.model_name as string,
            is_active: provider.is_active as boolean,
            priority: (provider.priority_order ?? provider.priority) as number,
            created_at: provider.created_at as string,
            updated_at: provider.updated_at as string,
            supported_features: config?.supported_features as
              | string[]
              | undefined,
            max_tokens: (config?.max_tokens as number) || 4096,
            rate_limit: (provider.rate_limit_rpm as number) || 60,
          } as LLMProvider & {
            supported_features?: string[];
            max_tokens?: number;
            rate_limit?: number;
          },
          is_enabled: (provider.is_enabled_for_tenant as boolean) || false,
          credentials_configured: (provider.is_configured as boolean) || false,
          health_status:
            (provider.health_status as ProviderConfig["health_status"]) ||
            "unavailable",
          health_latency_ms: provider.health_latency_ms as number | undefined,
          models: (provider.models as unknown[]) || [],
        };
      });
      setProviders(providerData);
    } catch (error) {
      console.error("Failed to load providers:", error);
      // Set some default providers for demonstration
      setProviders([
        {
          provider: {
            id: "openai",
            organisation_id: "",
            provider_name: "OpenAI",
            provider_type: "openai",
            model_name: "gpt-4",
            is_active: true,
            priority: 1,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            supported_features: ["chat", "embeddings", "function_calling"],
            max_tokens: 128000,
            rate_limit: 500,
          } as LLMProvider & {
            supported_features?: string[];
            max_tokens?: number;
            rate_limit?: number;
          },
          is_enabled: false,
          credentials_configured: false,
          health_status: "unavailable",
          models: [],
        },
        {
          provider: {
            id: "azure-openai",
            organisation_id: "",
            provider_name: "Azure OpenAI",
            provider_type: "azure_openai",
            model_name: "gpt-4",
            is_active: true,
            priority: 2,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            supported_features: ["chat", "embeddings", "function_calling"],
            max_tokens: 128000,
            rate_limit: 1000,
          } as LLMProvider & {
            supported_features?: string[];
            max_tokens?: number;
            rate_limit?: number;
          },
          is_enabled: false,
          credentials_configured: false,
          health_status: "unavailable",
          models: [],
        },
        {
          provider: {
            id: "anthropic",
            organisation_id: "",
            provider_name: "Anthropic",
            provider_type: "anthropic",
            model_name: "claude-3-opus",
            is_active: true,
            priority: 3,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            supported_features: ["chat", "vision"],
            max_tokens: 200000,
            rate_limit: 100,
          } as LLMProvider & {
            supported_features?: string[];
            max_tokens?: number;
            rate_limit?: number;
          },
          is_enabled: false,
          credentials_configured: false,
          health_status: "unavailable",
          models: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async () => {
    if (!token) return {};
    try {
      const providersResp = await automationApi.llmConfig.listProviders(token);
      const modelsMap: Record<string, unknown> = {};

      for (const provider of providersResp as unknown[]) {
        const p = provider as Record<string, unknown>;
        try {
          const modelsResp = await automationApi.llmConfig.getModels(
            token,
            p.id as string,
          );
          (modelsResp as unknown[]).forEach((model: unknown) => {
            const m = model as Record<string, unknown>;
            modelsMap[m.id as string] = {
              ...m,
              provider_name: p.name || p.id,
              provider_id: p.id,
            };
          });
        } catch (err) {
          console.warn(
            `Failed to load models for provider ${p.name || p.id}:`,
            err,
          );
        }
      }

      setModels(modelsMap);
      return modelsMap;
    } catch (error) {
      console.error("Failed to load models:", error);
      return {};
    }
  };

  const loadPreferences = async (modelsMap?: Record<string, unknown>) => {
    if (!token) return;
    try {
      const response = await automationApi.llmConfig.getPreferences(token);
      const modelLookup = modelsMap || models;
      const transformedPrefs = (response as unknown[]).map((pref: unknown) => {
        const p = pref as Record<string, unknown>;
        const preferredIds = p.preferred_model_ids as string[] | undefined;
        const fallbackIds = p.fallback_model_ids as string[] | undefined;
        return {
          task_type: p.task_type as string,
          primary_model: preferredIds?.[0] || "",
          primary_model_name:
            ((modelLookup[preferredIds?.[0] || ""] as Record<string, unknown>)
              ?.display_name as string) ||
            preferredIds?.[0] ||
            "N/A",
          fallback_models: fallbackIds || [],
          fallback_model_names: (fallbackIds || []).map(
            (id: string) =>
              ((modelLookup[id] as Record<string, unknown>)
                ?.display_name as string) || id,
          ),
          quality_tier:
            (p.quality_tier as string) ||
            ((modelLookup[preferredIds?.[0] || ""] as Record<string, unknown>)
              ?.quality_tier as string) ||
            "standard",
          max_retries: (p.max_retries as number) || 3,
        };
      });
      setPreferences(transformedPrefs);
    } catch (error) {
      console.error("Failed to load preferences:", error);
      setPreferences([]);
    }
  };

  const loadSystemDefaults = async (modelsMap?: Record<string, unknown>) => {
    if (!token) return;
    try {
      const response = await automationApi.llmConfig.getSystemDefaults(token);
      const modelLookup = modelsMap || models;
      const transformedDefaults = (response as unknown[]).map(
        (pref: unknown) => {
          const p = pref as Record<string, unknown>;
          const preferredIds = p.preferred_model_ids as string[] | undefined;
          const fallbackIds = p.fallback_model_ids as string[] | undefined;
          return {
            task_type: p.task_type as string,
            primary_model: preferredIds?.[0] || "",
            primary_model_name:
              ((modelLookup[preferredIds?.[0] || ""] as Record<string, unknown>)
                ?.display_name as string) ||
              preferredIds?.[0] ||
              "N/A",
            fallback_models: fallbackIds || [],
            fallback_model_names: (fallbackIds || []).map(
              (id: string) =>
                ((modelLookup[id] as Record<string, unknown>)
                  ?.display_name as string) || id,
            ),
            quality_tier:
              (p.quality_tier as string) ||
              ((modelLookup[preferredIds?.[0] || ""] as Record<string, unknown>)
                ?.quality_tier as string) ||
              "standard",
            max_retries: (p.max_retries as number) || 3,
          };
        },
      );
      setSystemDefaults(transformedDefaults);
    } catch (error) {
      console.error("Failed to load system defaults:", error);
      setSystemDefaults([]);
    }
  };

  const loadUsageData = async () => {
    if (!token) return;
    try {
      const [summaryResponse, providerResponse, modelResponse] =
        await Promise.all([
          automationApi.llmConfig.getUsageSummary(token),
          automationApi.llmConfig.getUsageByProvider(token),
          automationApi.llmConfig.getUsageByModel(
            token,
            selectedProviderFilter || undefined,
          ),
        ]);
      setUsageSummary(summaryResponse);
      setUsageByProvider(
        providerResponse as Array<{
          provider_id: string;
          provider_name: string;
          total_calls: number;
          total_tokens: number;
          prompt_tokens: number;
          completion_tokens: number;
          avg_latency_ms: number;
          total_cost: number;
        }>,
      );
      setUsageByModel(
        modelResponse as Array<{
          model_id: string;
          model_name: string;
          display_name: string;
          provider_name: string;
          total_calls: number;
          total_tokens: number;
          prompt_tokens: number;
          completion_tokens: number;
          avg_latency_ms: number;
          total_cost: number;
        }>,
      );
    } catch (error) {
      console.error("Failed to load usage data:", error);
      setUsageSummary(null);
      setUsageByProvider([]);
      setUsageByModel([]);
    }
  };

  const loadQuorumRules = async () => {
    if (!token) return;
    try {
      const rules = await automationApi.llmConfig.getQuorumRules(token);
      setQuorumRules(rules);
    } catch (error) {
      console.error("Failed to load quorum rules:", error);
      // Mock data for demonstration
      setQuorumRules([
        {
          id: "1",
          task_type: "content_generation",
          quorum_size: 2,
          consensus_strategy: "majority",
          confidence_threshold: 0.6,
          participants: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_editable: true,
          is_system_default: false,
          participant_models: [],
        },
        {
          id: "2",
          task_type: "classification",
          quorum_size: 3,
          consensus_strategy: "unanimous",
          confidence_threshold: 1.0,
          participants: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_editable: true,
          is_system_default: false,
          participant_models: [],
        },
      ]);
    }
  };

  const loadConsensusStrategies = async () => {
    if (!token) return;
    try {
      const strategies =
        await automationApi.llmConfig.getConsensusStrategies(token);
      setConsensusStrategies(strategies);
    } catch (error) {
      console.error("Failed to load consensus strategies:", error);
      setConsensusStrategies([
        "majority",
        "unanimous",
        "weighted",
        "first_response",
      ]);
    }
  };

  const handleUpdateQuorumRule = async (
    ruleId: string,
    updates: Partial<QuorumRule>,
  ) => {
    if (!token) return;
    try {
      await automationApi.llmConfig.updateQuorumRule(token, ruleId, updates);
      await loadQuorumRules();
      setEditingQuorumRule(null);
      toast.success("Quorum rule updated");
    } catch (error) {
      console.error("Failed to update quorum rule:", error);
      toast.error("Failed to update quorum rule");
    }
  };

  const formatProviderLabel = (providerName: string): string => {
    if (providerName === "anthropic") return "Anthropic";
    if (providerName === "openai") return "OpenAI";
    if (providerName.includes("foundry") || providerName.startsWith("azure"))
      return "Azure";
    return providerName;
  };

  const getModelsByTier = () => {
    const tiers: Record<string, unknown[]> = {
      flagship: [],
      premium: [],
      standard: [],
      economy: [],
    };

    Object.values(models).forEach((model) => {
      const m = model as Record<string, unknown>;
      const tier = (m.quality_tier as string) || "standard";
      if (tiers[tier]) {
        tiers[tier].push(model);
      }
    });

    // Sort within each tier by provider then display name
    for (const tier of Object.keys(tiers)) {
      tiers[tier].sort((a, b) => {
        const ma = a as Record<string, unknown>;
        const mb = b as Record<string, unknown>;
        const provA = formatProviderLabel(
          (ma.provider_name as string) || "",
        );
        const provB = formatProviderLabel(
          (mb.provider_name as string) || "",
        );
        if (provA !== provB) return provA.localeCompare(provB);
        return ((ma.display_name as string) || "").localeCompare(
          (mb.display_name as string) || "",
        );
      });
    }

    return tiers;
  };

  const handleUpdatePreference = async (
    taskType: string,
    field: "primary_model" | "fallback_models",
    value: unknown,
  ) => {
    if (!token) return;
    try {
      const updates: Record<string, unknown> = {};

      if (field === "primary_model") {
        updates.preferred_model_ids = [value];
      } else if (field === "fallback_models") {
        updates.fallback_model_ids = value;
      }

      await automationApi.llmConfig.updatePreferences(token, taskType, updates);
      await loadPreferences();

      toast.success("Preferences Updated", {
        description: `Model preference for ${taskType} has been updated`,
      });
    } catch (error) {
      console.error("Failed to update preference:", error);
      toast.error("Update Failed", {
        description: "Failed to update model preference",
      });
    }
  };

  const handleUpdateSystemDefault = async (
    taskType: string,
    field: "primary_model" | "fallback_models",
    value: unknown,
  ) => {
    if (!token) return;
    try {
      const updates: Record<string, unknown> = {};

      if (field === "primary_model") {
        updates.preferred_model_ids = [value];
      } else if (field === "fallback_models") {
        updates.fallback_model_ids = value;
      }

      await automationApi.llmConfig.updateSystemDefault(
        token,
        taskType,
        updates,
      );
      await loadSystemDefaults();

      toast.success("System Default Updated", {
        description: `Global default for ${taskType} has been updated`,
      });
    } catch (error) {
      console.error("Failed to update system default:", error);
      toast.error("Update Failed", {
        description: "Failed to update system default",
      });
    }
  };

  const handleEnableProvider = async (provider: ProviderConfig) => {
    if (!token) return;
    try {
      setLoading(true);
      const providerId = provider.provider.id;
      await automationApi.llmConfig.enableProvider(token, providerId);
      toast.success(
        provider.is_enabled ? "Provider Disabled" : "Provider Enabled",
        {
          description: `${provider.provider.provider_name} has been ${provider.is_enabled ? "disabled" : "enabled"}`,
        },
      );
      loadProviders();
    } catch (error) {
      console.error("Failed to enable provider:", error);
      toast.error("Error", {
        description: "Failed to enable provider",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestProvider = async (provider: ProviderConfig) => {
    if (!token) return;
    try {
      setLoading(true);
      const result = await automationApi.llmConfig.testProvider(
        token,
        provider.provider.id,
      );

      toast[result.success ? "success" : "error"](
        result.success ? "Connection Successful" : "Connection Failed",
        {
          description: result.message,
        },
      );

      await loadProviders();
    } catch (error) {
      console.error("Failed to test provider:", error);
      toast.error("Test Failed", {
        description: "Failed to test provider connection",
      });
    } finally {
      setLoading(false);
    }
  };

  const getHealthBadge = (status?: string, latencyMs?: number) => {
    const variants: Record<
      string,
      {
        variant: "default" | "secondary";
        className?: string;
        icon: typeof CheckCircle;
        text: string;
      }
    > = {
      active: { variant: "default", icon: CheckCircle, text: "Healthy" },
      healthy: { variant: "default", icon: CheckCircle, text: "Healthy" },
      degraded: { variant: "secondary", icon: AlertCircle, text: "Degraded" },
      unavailable: {
        variant: "secondary",
        className: "bg-red-100 text-red-800 border-red-200",
        icon: AlertCircle,
        text: "Unavailable",
      },
      unknown: { variant: "secondary", icon: AlertCircle, text: "Unknown" },
    };
    const config = variants[status || "unavailable"] || variants["unknown"];
    const Icon = config.icon;

    const showLatency =
      latencyMs && (status === "active" || status === "healthy");
    const displayText = showLatency
      ? `${config.text} (${latencyMs}ms)`
      : config.text;

    return (
      <Badge
        variant={config.variant}
        className={`gap-1 ${config.className || ""}`}
      >
        <Icon className="h-3 w-3" />
        {displayText}
      </Badge>
    );
  };

  const getProviderIcon = (type: string) => {
    const icons: Record<string, typeof Brain> = {
      openai: Brain,
      azure: Shield,
      anthropic: Zap,
      google: Brain,
      cohere: Brain,
    };
    const Icon = icons[type] || Brain;
    return <Icon className="h-4 w-4" />;
  };

  const extendedProvider = (p: ProviderConfig["provider"]) =>
    p as LLMProvider & {
      supported_features?: string[];
      max_tokens?: number;
      rate_limit?: number;
      description?: string;
    };

  const formatProviderType = (name: string, type: string): string => {
    const labels: Record<string, string> = {
      anthropic: "Anthropic (Direct API)",
      openai: "OpenAI",
      azure_openai: "Azure OpenAI",
      azure_anthropic: "Azure Anthropic",
    };
    if (name.includes("foundry")) {
      if (name.includes("anthropic")) return "Azure AI Foundry (Anthropic)";
      return "Azure AI Foundry";
    }
    return labels[type] || type;
  };

  const getModelCountForProvider = (providerId: string): number => {
    return Object.values(models).filter(
      (m) => (m as Record<string, unknown>).provider_id === providerId,
    ).length;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">LLM Configuration</h1>
          <p className="mt-2 text-lg text-slate-600">
            Configure language model providers and preferences
          </p>
        </div>
        <Button variant="outline" onClick={loadProviders} disabled={loading} className="rounded-full">
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="providers">
        <TabsList>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          {showSystemConfig && (
            <TabsTrigger value="system-defaults">System Defaults</TabsTrigger>
          )}
          <TabsTrigger value="preferences">Org Preferences</TabsTrigger>
          <TabsTrigger value="quorum">Quorum Rules</TabsTrigger>
          {showSystemConfig && (
            <TabsTrigger value="usage">Usage & Costs</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="providers">
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
            {providers.map((config) => (
              <div key={config.provider.id} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getProviderIcon(config.provider.provider_type)}
                    <div>
                      <h3 className="font-semibold">{config.provider.provider_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {extendedProvider(config.provider).description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {config.is_enabled &&
                      getHealthBadge(
                        config.health_status,
                        config.health_latency_ms,
                      )}
                    <Switch
                      checked={config.is_enabled}
                      onCheckedChange={() => handleEnableProvider(config)}
                      disabled={loading || !config.credentials_configured}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6 text-sm text-muted-foreground">
                    <span>
                      {formatProviderType(
                        config.provider.provider_name,
                        config.provider.provider_type,
                      )}
                    </span>
                    <span>
                      Priority: {config.provider.priority}
                    </span>
                    <span>
                      {getModelCountForProvider(config.provider.id)} models
                    </span>
                    {!config.credentials_configured && (
                      <Badge variant="secondary">
                        Credentials not configured
                      </Badge>
                    )}
                  </div>
                  {config.credentials_configured && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestProvider(config)}
                      disabled={loading}
                    >
                      <TestTube className="mr-2 h-4 w-4" />
                      Test
                    </Button>
                  )}
                </div>

                {extendedProvider(config.provider).supported_features &&
                  extendedProvider(config.provider).supported_features!
                    .length > 0 && (
                    <div className="flex gap-2">
                      <span className="text-sm text-muted-foreground">
                        Features:
                      </span>
                      {extendedProvider(
                        config.provider,
                      ).supported_features!.map((feature: string) => (
                        <Badge key={feature} variant="outline">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  )}

                {config.models && config.models.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium">
                      Available Models:
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      {config.models.slice(0, 4).map((model: unknown) => {
                        const m = model as Record<string, unknown>;
                        return (
                          <div
                            key={m.id as string}
                            className="flex items-center justify-between text-sm"
                          >
                            <span>{m.name as string}</span>
                            <Badge variant="outline">
                              {m.quality_tier as string}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        {showSystemConfig && (
          <TabsContent value="system-defaults">
            <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
              <div>
                <h3 className="text-lg font-semibold">System Default Models</h3>
                <p className="text-sm text-muted-foreground">
                  Global defaults for all organisations. Organisations inherit
                  these unless they have custom preferences.
                </p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task Type</TableHead>
                    <TableHead>Default Primary Model</TableHead>
                    <TableHead>Fallback Models</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {systemDefaults.map((pref) => {
                    const modelsByTier = getModelsByTier();

                    return (
                      <TableRow key={pref.task_type}>
                        <TableCell className="font-medium capitalize">
                          {(pref.task_type || "").replace(/_/g, " ")}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={pref.primary_model}
                            onValueChange={(value) =>
                              handleUpdateSystemDefault(
                                pref.task_type,
                                "primary_model",
                                value,
                              )
                            }
                          >
                            <SelectTrigger className="w-[280px]">
                              <SelectValue placeholder="Select primary model" />
                            </SelectTrigger>
                            <SelectContent>
                              {[
                                "flagship",
                                "premium",
                                "standard",
                                "economy",
                              ].map((tier) => {
                                const tierModels = modelsByTier[tier] || [];
                                if (tierModels.length === 0) return null;
                                return (
                                  <SelectGroup key={tier}>
                                    <SelectLabel className="capitalize">
                                      {tier}
                                    </SelectLabel>
                                    {tierModels.map((model) => {
                                      const m = model as Record<
                                        string,
                                        unknown
                                      >;
                                      const provLabel = formatProviderLabel(
                                        (m.provider_name as string) || "",
                                      );
                                      return (
                                        <SelectItem
                                          key={m.id as string}
                                          value={m.id as string}
                                        >
                                          <span className="flex items-center gap-2">
                                            <span>
                                              {m.display_name as string}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                              {provLabel}
                                            </span>
                                          </span>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectGroup>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1">
                            {pref.fallback_models.map((id, idx) => {
                              return (
                                <Badge
                                  key={id}
                                  variant="outline"
                                  className="gap-1 pr-1"
                                >
                                  {pref.fallback_model_names[idx] || id}
                                  <button
                                    className="ml-1 rounded-full hover:bg-muted"
                                    onClick={() => {
                                      const updated =
                                        pref.fallback_models.filter(
                                          (fid) => fid !== id,
                                        );
                                      handleUpdateSystemDefault(
                                        pref.task_type,
                                        "fallback_models",
                                        updated,
                                      );
                                    }}
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              );
                            })}
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 gap-1 px-2 text-xs"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-[260px] p-0"
                                align="start"
                              >
                                <Command>
                                  <CommandInput placeholder="Search models..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No models found.
                                    </CommandEmpty>
                                    {[
                                      "flagship",
                                      "premium",
                                      "standard",
                                      "economy",
                                    ].map((tier) => {
                                      const tierModels =
                                        modelsByTier[tier] || [];
                                      if (tierModels.length === 0) return null;
                                      return (
                                        <CommandGroup
                                          key={tier}
                                          heading={
                                            tier.charAt(0).toUpperCase() +
                                            tier.slice(1)
                                          }
                                        >
                                          {tierModels.map((model) => {
                                            const m = model as Record<
                                              string,
                                              unknown
                                            >;
                                            const modelId = m.id as string;
                                            const isSelected =
                                              pref.fallback_models.includes(
                                                modelId,
                                              );
                                            const isPrimary =
                                              pref.primary_model === modelId;
                                            if (isPrimary) return null;
                                            return (
                                              <CommandItem
                                                key={modelId}
                                                value={`${m.display_name as string} ${formatProviderLabel((m.provider_name as string) || "")}`}
                                                onSelect={() => {
                                                  const updated = isSelected
                                                    ? pref.fallback_models.filter(
                                                        (fid) =>
                                                          fid !== modelId,
                                                      )
                                                    : [
                                                        ...pref.fallback_models,
                                                        modelId,
                                                      ];
                                                  handleUpdateSystemDefault(
                                                    pref.task_type,
                                                    "fallback_models",
                                                    updated,
                                                  );
                                                }}
                                              >
                                                <Check
                                                  className={`mr-2 h-4 w-4 ${isSelected ? "opacity-100" : "opacity-0"}`}
                                                />
                                                <span>
                                                  {m.display_name as string}
                                                </span>
                                                <span className="ml-auto text-xs text-muted-foreground">
                                                  {formatProviderLabel(
                                                    (m.provider_name as string) ||
                                                      "",
                                                  )}
                                                </span>
                                              </CommandItem>
                                            );
                                          })}
                                        </CommandGroup>
                                      );
                                    })}
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}

        <TabsContent value="preferences">
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
            <div>
              <h3 className="text-lg font-semibold">Organisation Model Preferences</h3>
              <p className="text-sm text-muted-foreground">
                Custom preferences for this organisation. Leave empty to use
                system defaults.
              </p>
            </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task Type</TableHead>
                    <TableHead>Primary Model</TableHead>
                    <TableHead>Fallback Models</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preferences.map((pref) => {
                    const modelsByTier = getModelsByTier();

                    return (
                      <TableRow key={pref.task_type}>
                        <TableCell className="font-medium capitalize">
                          {(pref.task_type || "").replace(/_/g, " ")}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={pref.primary_model}
                            onValueChange={(value) =>
                              handleUpdatePreference(
                                pref.task_type,
                                "primary_model",
                                value,
                              )
                            }
                          >
                            <SelectTrigger className="w-[280px]">
                              <SelectValue placeholder="Select primary model" />
                            </SelectTrigger>
                            <SelectContent>
                              {[
                                "flagship",
                                "premium",
                                "standard",
                                "economy",
                              ].map((tier) => {
                                const tierModels = modelsByTier[tier] || [];
                                if (tierModels.length === 0) return null;
                                return (
                                  <SelectGroup key={tier}>
                                    <SelectLabel className="capitalize">
                                      {tier}
                                    </SelectLabel>
                                    {tierModels.map((model) => {
                                      const m = model as Record<
                                        string,
                                        unknown
                                      >;
                                      const provLabel = formatProviderLabel(
                                        (m.provider_name as string) || "",
                                      );
                                      return (
                                        <SelectItem
                                          key={m.id as string}
                                          value={m.id as string}
                                        >
                                          <span className="flex items-center gap-2">
                                            <span>
                                              {m.display_name as string}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                              {provLabel}
                                            </span>
                                          </span>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectGroup>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1">
                            {pref.fallback_models.map((id, idx) => {
                              return (
                                <Badge
                                  key={id}
                                  variant="outline"
                                  className="gap-1 pr-1"
                                >
                                  {pref.fallback_model_names[idx] || id}
                                  <button
                                    className="ml-1 rounded-full hover:bg-muted"
                                    onClick={() => {
                                      const updated =
                                        pref.fallback_models.filter(
                                          (fid) => fid !== id,
                                        );
                                      handleUpdatePreference(
                                        pref.task_type,
                                        "fallback_models",
                                        updated,
                                      );
                                    }}
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              );
                            })}
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 gap-1 px-2 text-xs"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-[260px] p-0"
                                align="start"
                              >
                                <Command>
                                  <CommandInput placeholder="Search models..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      No models found.
                                    </CommandEmpty>
                                    {[
                                      "flagship",
                                      "premium",
                                      "standard",
                                      "economy",
                                    ].map((tier) => {
                                      const tierModels =
                                        modelsByTier[tier] || [];
                                      if (tierModels.length === 0) return null;
                                      return (
                                        <CommandGroup
                                          key={tier}
                                          heading={
                                            tier.charAt(0).toUpperCase() +
                                            tier.slice(1)
                                          }
                                        >
                                          {tierModels.map((model) => {
                                            const m = model as Record<
                                              string,
                                              unknown
                                            >;
                                            const modelId = m.id as string;
                                            const isSelected =
                                              pref.fallback_models.includes(
                                                modelId,
                                              );
                                            const isPrimary =
                                              pref.primary_model === modelId;
                                            if (isPrimary) return null;
                                            return (
                                              <CommandItem
                                                key={modelId}
                                                value={`${m.display_name as string} ${formatProviderLabel((m.provider_name as string) || "")}`}
                                                onSelect={() => {
                                                  const updated = isSelected
                                                    ? pref.fallback_models.filter(
                                                        (fid) =>
                                                          fid !== modelId,
                                                      )
                                                    : [
                                                        ...pref.fallback_models,
                                                        modelId,
                                                      ];
                                                  handleUpdatePreference(
                                                    pref.task_type,
                                                    "fallback_models",
                                                    updated,
                                                  );
                                                }}
                                              >
                                                <Check
                                                  className={`mr-2 h-4 w-4 ${isSelected ? "opacity-100" : "opacity-0"}`}
                                                />
                                                <span>
                                                  {m.display_name as string}
                                                </span>
                                                <span className="ml-auto text-xs text-muted-foreground">
                                                  {formatProviderLabel(
                                                    (m.provider_name as string) ||
                                                      "",
                                                  )}
                                                </span>
                                              </CommandItem>
                                            );
                                          })}
                                        </CommandGroup>
                                      );
                                    })}
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
          </div>
        </TabsContent>

        <TabsContent value="quorum">
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Quorum Rules
              </h3>
              <p className="text-sm text-muted-foreground">
                Configure multi-model consensus rules for critical tasks. When
                enabled, multiple models must agree before a response is
                accepted.
              </p>
            </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead direction={getQuorumSortDir("task_type")} onSort={() => requestQuorumSort("task_type")}>Task Type</SortableTableHead>
                    <SortableTableHead direction={getQuorumSortDir("quorum_size")} onSort={() => requestQuorumSort("quorum_size")}>Quorum Size</SortableTableHead>
                    <SortableTableHead direction={getQuorumSortDir("consensus_strategy")} onSort={() => requestQuorumSort("consensus_strategy")}>Strategy</SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>Participants</SortableTableHead>
                    <SortableTableHead direction={getQuorumSortDir("confidence_threshold")} onSort={() => requestQuorumSort("confidence_threshold")}>Confidence</SortableTableHead>
                    <SortableTableHead direction={getQuorumSortDir("is_system_default")} onSort={() => requestQuorumSort("is_system_default")}>Scope</SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">{" "}</SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedQuorumRules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell className="font-medium">
                        {(rule.task_type || "")
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </TableCell>
                      <TableCell>{rule.quorum_size}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {(rule.consensus_strategy || "").replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(rule.participant_models || []).map((pm) => {
                            const modelData = models[pm.id] as
                              | Record<string, unknown>
                              | undefined;
                            const provLabel = modelData
                              ? formatProviderLabel(
                                  (modelData.provider_name as string) || "",
                                )
                              : "";
                            return (
                              <Badge
                                key={pm.id}
                                variant="secondary"
                                className="text-xs"
                              >
                                {pm.name}
                                {provLabel && (
                                  <span className="ml-1 text-muted-foreground">
                                    {provLabel}
                                  </span>
                                )}
                              </Badge>
                            );
                          })}
                          {(!rule.participant_models ||
                            rule.participant_models.length === 0) && (
                            <span className="text-xs text-muted-foreground">
                              Auto-selected
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {Math.round((rule.confidence_threshold || 0) * 100)}%
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            rule.is_system_default ? "secondary" : "default"
                          }
                        >
                          {rule.is_system_default ? "System" : "Custom"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {rule.is_editable && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingQuorumRule(rule)}
                          >
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {quorumRules.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No quorum rules configured</p>
                  <p className="text-sm">
                    Quorum rules ensure multi-model agreement for critical tasks
                  </p>
                </div>
              )}

            <h4 className="text-sm font-semibold mt-2">Consensus Strategies</h4>
            <p className="text-xs text-muted-foreground mb-2">
              Available strategies for multi-model consensus
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              {consensusStrategies.map((strategy) => (
                <div key={strategy} className="border rounded-lg p-4">
                  <h4 className="font-medium capitalize">
                    {(strategy || "").replace(/_/g, " ")}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {strategy === "majority" &&
                      "Response accepted when majority of models agree"}
                    {strategy === "unanimous" &&
                      "All models must return the same response"}
                    {strategy === "weighted" &&
                      "Models are weighted by confidence scores"}
                    {strategy === "first_response" &&
                      "First valid response is accepted"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {showSystemConfig && (
          <TabsContent value="usage">
            <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between pb-2">
                    <span className="text-sm font-medium">Total Tokens Used</span>
                    <Brain className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="text-2xl font-bold">
                    {usageSummary
                      ? usageSummary.total_tokens.toLocaleString()
                      : "0"}
                  </div>
                  <p className="text-xs text-muted-foreground">This month</p>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between pb-2">
                    <span className="text-sm font-medium">API Calls</span>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="text-2xl font-bold">
                    {usageSummary
                      ? usageSummary.total_calls.toLocaleString()
                      : "0"}
                  </div>
                  <p className="text-xs text-muted-foreground">This month</p>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between pb-2">
                    <span className="text-sm font-medium">Estimated Cost</span>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="text-2xl font-bold">
                    {usageSummary ? formatCost(usageSummary.total_cost) : "$0.00"}
                  </div>
                  <p className="text-xs text-muted-foreground">This month</p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold">Usage by Provider</h3>
                <p className="text-sm text-muted-foreground">
                  Token usage and costs breakdown by provider
                </p>
              </div>
              {usageByProvider.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Info className="h-12 w-12 mx-auto mb-4" />
                  <p>No usage data available yet</p>
                  <p className="text-sm">
                    Usage statistics will appear here once you start using the
                    LLM providers
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead direction={getProviderSortDir("provider_name")} onSort={() => requestProviderSort("provider_name")}>Provider</SortableTableHead>
                      <SortableTableHead direction={getProviderSortDir("total_calls")} onSort={() => requestProviderSort("total_calls")} className="text-right">Calls</SortableTableHead>
                      <SortableTableHead direction={getProviderSortDir("total_tokens")} onSort={() => requestProviderSort("total_tokens")} className="text-right">Tokens</SortableTableHead>
                      <SortableTableHead direction={getProviderSortDir("prompt_tokens")} onSort={() => requestProviderSort("prompt_tokens")} className="text-right">Prompt</SortableTableHead>
                      <SortableTableHead direction={getProviderSortDir("completion_tokens")} onSort={() => requestProviderSort("completion_tokens")} className="text-right">Completion</SortableTableHead>
                      <SortableTableHead direction={getProviderSortDir("avg_latency_ms")} onSort={() => requestProviderSort("avg_latency_ms")} className="text-right">Avg Latency</SortableTableHead>
                      <SortableTableHead direction={getProviderSortDir("total_cost")} onSort={() => requestProviderSort("total_cost")} className="text-right">Cost</SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedUsageByProvider.map((provider) => (
                      <TableRow key={provider.provider_id}>
                        <TableCell className="font-medium">
                          {provider.provider_name}
                        </TableCell>
                        <TableCell className="text-right">
                          {provider.total_calls.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {provider.total_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {provider.prompt_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {provider.completion_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {provider.avg_latency_ms}ms
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCost(provider.total_cost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <div className="flex items-center justify-between mt-4">
                <div>
                  <h3 className="text-lg font-semibold">Usage by Model</h3>
                  <p className="text-sm text-muted-foreground">
                    Token usage and costs breakdown by model
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-muted-foreground">
                    Filter by Provider:
                  </label>
                  <select
                    value={selectedProviderFilter}
                    onChange={(e) => setSelectedProviderFilter(e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                  >
                    <option value="">All Providers</option>
                    {usageByProvider.map((provider) => (
                      <option
                        key={provider.provider_id}
                        value={provider.provider_id}
                      >
                        {provider.provider_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {usageByModel.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Info className="h-12 w-12 mx-auto mb-4" />
                  <p>No model usage data available yet</p>
                  <p className="text-sm">
                    Model statistics will appear here once you start using the
                    LLM providers
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead direction={getModelSortDir("display_name")} onSort={() => requestModelSort("display_name")}>Model</SortableTableHead>
                      <SortableTableHead direction={getModelSortDir("provider_name")} onSort={() => requestModelSort("provider_name")}>Provider</SortableTableHead>
                      <SortableTableHead direction={getModelSortDir("total_calls")} onSort={() => requestModelSort("total_calls")} className="text-right">Calls</SortableTableHead>
                      <SortableTableHead direction={getModelSortDir("total_tokens")} onSort={() => requestModelSort("total_tokens")} className="text-right">Tokens</SortableTableHead>
                      <SortableTableHead direction={getModelSortDir("prompt_tokens")} onSort={() => requestModelSort("prompt_tokens")} className="text-right">Prompt</SortableTableHead>
                      <SortableTableHead direction={getModelSortDir("completion_tokens")} onSort={() => requestModelSort("completion_tokens")} className="text-right">Completion</SortableTableHead>
                      <SortableTableHead direction={getModelSortDir("avg_latency_ms")} onSort={() => requestModelSort("avg_latency_ms")} className="text-right">Avg Latency</SortableTableHead>
                      <SortableTableHead direction={getModelSortDir("total_cost")} onSort={() => requestModelSort("total_cost")} className="text-right">Cost</SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedUsageByModel.map((model) => (
                      <TableRow key={model.model_id}>
                        <TableCell className="font-medium">
                          {model.display_name || model.model_name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {model.provider_name}
                        </TableCell>
                        <TableCell className="text-right">
                          {model.total_calls.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {model.total_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {model.prompt_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {model.completion_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {model.avg_latency_ms}ms
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCost(model.total_cost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Quorum Rule Edit Dialog */}
      <Dialog
        open={!!editingQuorumRule}
        onOpenChange={(open) => !open && setEditingQuorumRule(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Quorum Rule</DialogTitle>
            <DialogDescription>
              Configure consensus requirements for{" "}
              {(editingQuorumRule?.task_type || "").replace(/_/g, " ")}
            </DialogDescription>
          </DialogHeader>
          {editingQuorumRule && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="quorum-size">Quorum Size</Label>
                <Input
                  id="quorum-size"
                  type="number"
                  min="2"
                  max="10"
                  value={editingQuorumRule.quorum_size}
                  onChange={(e) =>
                    setEditingQuorumRule({
                      ...editingQuorumRule,
                      quorum_size: parseInt(e.target.value),
                    })
                  }
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Number of models that must participate in consensus
                </p>
              </div>

              <div>
                <Label htmlFor="strategy">Consensus Strategy</Label>
                <Select
                  value={editingQuorumRule.consensus_strategy}
                  onValueChange={(value) =>
                    setEditingQuorumRule({
                      ...editingQuorumRule,
                      consensus_strategy: value,
                    })
                  }
                >
                  <SelectTrigger id="strategy" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {consensusStrategies.map((strategy) => (
                      <SelectItem key={strategy} value={strategy}>
                        {(strategy || "")
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="threshold">Confidence Threshold (%)</Label>
                <Input
                  id="threshold"
                  type="number"
                  min="10"
                  max="100"
                  value={Math.round(
                    (editingQuorumRule.confidence_threshold || 0.7) * 100,
                  )}
                  onChange={(e) =>
                    setEditingQuorumRule({
                      ...editingQuorumRule,
                      confidence_threshold: parseInt(e.target.value) / 100,
                    })
                  }
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Minimum confidence required for consensus
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setEditingQuorumRule(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    handleUpdateQuorumRule(editingQuorumRule.id, {
                      quorum_size: editingQuorumRule.quorum_size,
                      consensus_strategy: editingQuorumRule.consensus_strategy,
                      confidence_threshold:
                        editingQuorumRule.confidence_threshold,
                    })
                  }
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
